import { ApiHandler } from '../ApiHandler'
import { API_BASE_URL } from '@/shared/config/env'
import { Cookies } from '@/shared/helpers/Cookies'
import { deviceSupportsSecretCalls } from '@/shared/crypto/bootstrap'

export abstract class CallApi {
  // --- Запись звонков/встреч (серверная, через Egress). Только авторизованным. ---
  public static async recordingStart(roomName: string) {
    return await ApiHandler.post('calls/recording/start', { room_name: roomName })
  }
  public static async recordingStop(id: number) {
    return await ApiHandler.post('calls/recording/stop', { id })
  }
  public static async recordings() {
    return await ApiHandler.get('calls/recordings')
  }
  /** Гость оставляет E-mail, чтобы после встречи получить ссылку на запись. Публично. */
  public static async requestRecording(roomName: string, email: string) {
    return await ApiHandler.post('calls/recording/request', { room_name: roomName, email })
  }
  /** Удалить запись (файл на сервере + строку). На фронте — с подтверждением. */
  public static async deleteRecording(id: number) {
    return await ApiHandler.delete(`calls/recordings/${id}`, {})
  }
  /**
   * URL для просмотра записи прямо в <video>. Токен в query — тег <video> не умеет
   * слать заголовок Authorization; бэкенд отдаёт с поддержкой Range (перемотка).
   */
  public static streamUrl(id: number): string {
    const token = Cookies.get('authToken')
    const t = token ? `?token=${encodeURIComponent(token)}` : ''
    return `${API_BASE_URL}calls/recordings/${id}/stream${t}`
  }
  /** Скачать запись: тянем blob с Bearer-токеном и сохраняем на диск. */
  public static async downloadRecording(id: number, fileName: string) {
    const token = Cookies.get('authToken')
    const res = await fetch(`${API_BASE_URL}calls/recordings/${id}/download`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    })
    if (!res.ok) throw new Error('Не удалось скачать запись')
    const blob = await res.blob()
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = fileName || `запись-${id}.mp4`
    document.body.appendChild(a)
    a.click()
    a.remove()
    URL.revokeObjectURL(url)
  }

  public static async history() {
    return await ApiHandler.get(
      'calls/history', {page: 1},
    )
  }

  /**
   * Текущий незавершённый приватный звонок пользователя (звонящий/ринг/активный),
   * либо {call_id: null}. Нужно для ресинка после реконнекта ws: если входящий
   * звонок «прозвонил» пока сокет лежал, пуш потерян — этим запросом узнаём о нём.
   */
  public static async active() {
    return await ApiHandler.get('calls/active')
  }
  /** key_envelope — конверт ключа секретного звонка (только в секретном чате). */
  public static async initiate(chatId: number, params: {callee_id: number, call_type: string, key_envelope?: object}) {
    return await ApiHandler.post(
      `chats/${chatId}/call/initiate`,
      params,
    )
  }

  /** e2e_public_key — ключ этого устройства; обязателен для секретного звонка. */
  public static async accept(chatId: number, params: {call_id: number, e2e_public_key?: string}) {
    return await ApiHandler.post(
      `chats/${chatId}/call/accept`,
      params,
    )
  }

  public static async reject(chatId: number, params: {call_id: number}) {
    return await ApiHandler.post(
      `chats/${chatId}/call/reject`,
      params,
    )
  }

  public static async end(chatId: number, params: {call_id: number}) {
    return await ApiHandler.post(
      `chats/${chatId}/call/end`,
      params,
    )
  }

  public static async cancel(chatId: number, params: {call_id: number}) {
    return await ApiHandler.post(
      `chats/${chatId}/call/cancel`,
      params,
    )
  }

  /** Создать комнату-встречу (Zoom-подобную): вернёт токен организатора, url и invite_token для ссылки. */
  public static async createMeeting(params: {name?: string, waiting_room?: boolean} = {}) {
    return await ApiHandler.post('calls/meeting', params)
  }

  // --- Зал ожидания ---
  /**
   * Гость опрашивает статус входа (waiting/admitted/denied). При admitted вернёт token/url. Публично.
   * chatToken — прежний токен переписки встречи из CRM (stored): при впуске к ответу
   * добавляется chat: {token, my_tag}, с ним гость остаётся тем же автором.
   */
  public static async guestAdmissionStatus(token: string, chatToken?: string) {
    return await ApiHandler.get('calls/guest-admission', chatToken ? { t: token, chat_token: chatToken } : { t: token })
  }
  /** Список гостей, ждущих впуска в комнату (для хоста). */
  public static async waitingGuests(roomName: string) {
    return await ApiHandler.get('calls/waiting-guests', { room_name: roomName })
  }
  /** Хост впускает гостя. */
  public static async admitGuest(id: number) {
    return await ApiHandler.post(`calls/admission/${id}/admit`, {})
  }
  /** Хост отклоняет гостя. */
  public static async denyGuest(id: number) {
    return await ApiHandler.post(`calls/admission/${id}/deny`, {})
  }

  /** Список активных встреч (с участниками и числом гостей). */
  public static async activeRooms() {
    return await ApiHandler.get('calls/rooms/active')
  }

  /**
   * Что происходит в перечисленных комнатах прямо сейчас: {room: {exists, total}}.
   * Календарю нужно отличать «идёт» от «все вышли» — по времени этого не понять.
   */
  public static async roomsLive(roomNames: string[]) {
    return await ApiHandler.get('calls/rooms/live', { rooms: roomNames.join(',') })
  }

  /**
   * Войти в активную встречу — вернёт LiveKit url/token, room_name, invite_token.
   * `e2e` — браузер умеет шифровать кадры: без этого в зашифрованную комнату
   * сервер не пустит (docs/encryption/E2E-ROOM.md, §4).
   */
  public static async joinRoom(roomName: string) {
    return await ApiHandler.post('calls/rooms/join', { room_name: roomName, e2e: deviceSupportsSecretCalls() })
  }

  /**
   * Вход по ссылке-приглашению для СВОЕГО пользователя: без имени и без зала
   * ожидания. Гостевой путь (guestToken) остаётся для тех, кто не авторизован.
   */
  public static async joinByInvite(inviteToken: string) {
    return await ApiHandler.post('calls/rooms/join-by-invite', {
      invite_token: inviteToken,
      e2e: deviceSupportsSecretCalls(),
    })
  }

  /** Зашифрованная комната: войти за ключом разговора (E2E-ROOM.md, §3.2). */
  public static async e2eRoomEnter(roomName: string, deviceKey: string) {
    return await ApiHandler.post('calls/e2e-rooms/session', { room_name: roomName, device_key: deviceKey })
  }

  /** Ждать ключ по заявке: waiting | ready (конверт) | stale. */
  public static async e2eRoomRequestStatus(requestId: number, deviceKey: string) {
    return await ApiHandler.post(`calls/e2e-rooms/requests/${requestId}/status`, { device_key: deviceKey })
  }

  /** Держатель ключа: отметка «я здесь» и заявки на ключ. */
  public static async e2eRoomPoll(sessionId: string, deviceKey: string, roomName = '') {
    return await ApiHandler.post(`calls/e2e-rooms/sessions/${sessionId}/poll`, {
      device_key: deviceKey,
      ...(roomName ? { room_name: roomName } : {}),
    })
  }

  /** Держатель передаёт ключ входящему. */
  public static async e2eRoomAnswer(requestId: number, sessionId: string, deviceKey: string, envelope: object) {
    return await ApiHandler.post(`calls/e2e-rooms/requests/${requestId}/envelope`, {
      session_id: sessionId,
      device_key: deviceKey,
      envelope,
    })
  }

  /** Устройство ушло из комнаты. */
  public static async e2eRoomLeave(sessionId: string, deviceKey: string) {
    return await ApiHandler.post(`calls/e2e-rooms/sessions/${sessionId}/leave`, { device_key: deviceKey })
  }

  /** Вечные комнаты (постоянная ссылка). */
  public static async permanentRooms() {
    return await ApiHandler.get('calls/permanent-rooms')
  }
  /** Комнаты для кнопки быстрого входа (только руководству; остальным — пусто). */
  public static async quickRooms(): Promise<{ data: { room_name: string; title: string }[] }> {
    return await ApiHandler.get('calls/permanent-rooms/quick')
  }
  public static async createPermanentRoom(title: string, waitingRoom = false, hostUserIds: number[] = []) {
    return await ApiHandler.post('calls/permanent-rooms', {
      title,
      waiting_room: waitingRoom,
      host_user_ids: hostUserIds,
    })
  }
  /** Редактировать вечную комнату: название, зал ожидания, ответственные пользователи. */
  public static async updatePermanentRoom(
    id: number,
    payload: { title?: string; waiting_room?: boolean; host_user_ids?: number[] },
  ) {
    return await ApiHandler.patch(`calls/permanent-rooms/${id}`, payload)
  }
  public static async deletePermanentRoom(id: number) {
    return await ApiHandler.delete(`calls/permanent-rooms/${id}`, {})
  }

  /**
   * Короткая ссылка-приглашение на комнату: {code, url}.
   * Раньше тут дёргался `calls/invite`, которого на бэкенде нет (404) — из-за
   * этого кнопка «Пригласить» в звонке молча ничего не копировала.
   */
  public static async invite(params: {room_name: string}) {
    return await ApiHandler.post('calls/short-link', params)
  }

  /** ПУБЛИЧНО: короткий код из ссылки /j/<code> → обычный invite_token. */
  public static async resolveShortLink(code: string) {
    return await ApiHandler.get(`calls/short-link/${encodeURIComponent(code)}`)
  }

  /**
   * ПУБЛИЧНЫЙ вход гостя по ссылке: возвращает LiveKit-токен в комнату.
   * admission_token — токен уже созданной заявки в зал ожидания: при повторных
   * опросах он не даёт создать новую заявку и новое оповещение хосту.
   */
  public static async guestToken(params: {invite_token: string, name?: string, admission_token?: string}) {
    return await ApiHandler.post('calls/guest-token', params)
  }

  /** Завершить звонок для всех (удалить комнату LiveKit). */
  public static async endRoom(params: {room_name: string}) {
    return await ApiHandler.post('calls/end-room', params)
  }
}
